import React from 'react';
import { matchPath, Outlet, Route, Routes, useLocation } from 'react-router-dom';

import { AdminLayout } from 'components/layout/AdminLayout';
import { Footer } from 'components/layout/Footer';
import { NavigationLayout } from 'components/layout/NavigationLayout';
import { InAppMeetingReminderPoller } from 'components/notifications/InAppMeetingReminderPoller';
import { SnapshotDueGoalPrompt } from 'components/notifications/SnapshotDueGoalPrompt';
import { Toaster } from 'components/ui/toaster';
import { ThemedApp } from './ThemedApp';
import { ROUTE_GROUPS, MEETING_CALL_PATH, GuardComponent, HomeContentWidth, RouteEntry, RouteLayout } from './config';

type ResolvedRoute = RouteEntry & {
  guards: GuardComponent[];
};

const resolveRoutes = (): ResolvedRoute[] =>
  ROUTE_GROUPS.flatMap(({ guards, routes }) =>
    routes.map((route) => ({
      ...route,
      guards,
    })),
  );

const applyGuards = (element: React.ReactNode, guards: GuardComponent[]) =>
  (guards ?? []).reduceRight<React.ReactNode>(
    (acc, GuardComponent) => <GuardComponent>{acc}</GuardComponent>,
    element,
  );

const guardsMatch = (left: GuardComponent[], right: GuardComponent[]) =>
  left.length === right.length && left.every((guard, index) => guard === right[index]);

type LayoutRouteGroup = {
  layout: RouteLayout;
  contentWidth: HomeContentWidth;
  guards: GuardComponent[];
  routes: ResolvedRoute[];
};

const shouldShowFooter = (pathname: string) => {
  const hiddenExactPaths = new Set(['/flows']);
  if (hiddenExactPaths.has(pathname)) {
    return false;
  }

  if (pathname.startsWith('/flows/')) {
    return false;
  }

  return !/^\/courses\/[^/]+\/flows\//.test(pathname);
};

const HomeLayout: React.FC<{ children?: React.ReactNode; contentWidth: HomeContentWidth }> = ({
  children,
  contentWidth,
}) => (
  <NavigationLayout contentWidth={contentWidth}>{children}</NavigationLayout>
);

// Admin pages share one layout route so the sidebar (and its collapsed state) persists across admin navigation.
// Admin workspaces ignore the learner content widths and use the full screen.
const AdminWorkspaceLayout: React.FC<{ children?: React.ReactNode; contentWidth: HomeContentWidth }> = ({
  children,
}) => (
  <NavigationLayout contentWidth="full">
    <AdminLayout>{children}</AdminLayout>
  </NavigationLayout>
);

const LAYOUT_COMPONENTS: Record<RouteLayout, typeof HomeLayout> = {
  home: HomeLayout,
  admin: AdminWorkspaceLayout,
};

export const AppRoutes: React.FC = () => {
  const location = useLocation();
  const showFooter = shouldShowFooter(location.pathname);
  const meetingOwnsNotifications = Boolean(matchPath(MEETING_CALL_PATH, location.pathname));

  const allRoutes = React.useMemo(() => resolveRoutes(), []);

  const layoutRouteGroups = React.useMemo(() => {
    const groups: LayoutRouteGroup[] = [];

    allRoutes
      .filter((route): route is ResolvedRoute & { layout: RouteLayout } => Boolean(route.layout))
      .forEach((route) => {
        // Admin pages share one layout route regardless of contentWidth, which the admin layout ignores.
        const contentWidth = route.layout === 'admin' ? 'standard' : route.contentWidth ?? 'standard';
        const existingGroup = groups.find(
          (group) => group.layout === route.layout
            && group.contentWidth === contentWidth
            && guardsMatch(group.guards, route.guards),
        );

        if (existingGroup) {
          existingGroup.routes.push(route);
        } else {
          groups.push({
            layout: route.layout,
            contentWidth,
            guards: route.guards,
            routes: [route],
          });
        }
      });

    return groups;
  }, [allRoutes]);

  const routesWithoutLayout = allRoutes.filter((route) => !route.layout);

  return (
    <>
    <ThemedApp>
      <div className="flex flex-1 flex-col">
        <Routes>
          {routesWithoutLayout.map(({ path, element, guards }) => (
            <Route key={path} path={path} element={applyGuards(element, guards)} />
          ))}

          {layoutRouteGroups.map(({ layout, guards, routes, contentWidth }, index) => {
            const LayoutComponent = LAYOUT_COMPONENTS[layout];
            return (
              <Route
                key={`layout-${layout}-${contentWidth}-${guards.map((guard) => guard.name ?? 'guard').join('-')}-${index}`}
                element={applyGuards(
                  <LayoutComponent contentWidth={contentWidth}>
                    <Outlet />
                  </LayoutComponent>,
                  guards,
                )}
              >
                {routes.map(({ path, element }) => (
                  <Route key={path} path={path} element={element} />
                ))}
              </Route>
            );
          })}
        </Routes>

        {showFooter && <Footer />}
      </div>
      <InAppMeetingReminderPoller />
      {!meetingOwnsNotifications && <Toaster />}
    </ThemedApp>
    {/* Mobile customization renders inline; keep every check-in surface outside
        the learner theme just like its portal-rendered desktop counterpart. */}
    <SnapshotDueGoalPrompt />
    </>
  );
};
